import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Image,
  Text,
  useColorModeValue,
  Flex,
  Stack,
  Divider,
  Grid,
  GridItem,
  Modal,
  ModalOverlay,
  ModalHeader,
  ModalContent,
  ModalCloseButton,
  ModalBody,
  useDisclosure,
  Spinner,
  HStack,
  Skeleton
} from '@chakra-ui/react';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import {
  useGetUserDataMutation,
  useGetPointsExpiringSoonTransactionsMutation,
} from '../../slices/userApiSlice';
import { useTranslation } from 'react-i18next';
import ExpiringSoonPointHistory from '../../components/UserComponents/ExpiringSoonPointHistory';

const UserScanMe = () => {
  const { userInfo } = useSelector((state) => state.auth);
  const navigate = useNavigate();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [getUserData] = useGetUserDataMutation();
  const [getPointsExpiringSoonTransactions] = useGetPointsExpiringSoonTransactionsMutation();

  const [userData, setUserData] = useState(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [showExpiringSoon, setShowExpiringSoon] = useState(false);
  const [expiringSoonTransactions, setExpiringSoonTransactions] = useState([]);
  const [expiringLoading, setExpiringLoading] = useState(false);

  const { t } = useTranslation();

  // Colors
  const bgColor = useColorModeValue('teal.600', 'gray.800');
  const bgColorDark = useColorModeValue('gray.50', 'gray.700');
  const textColor = useColorModeValue('custom.900', 'gray.200');
  const navTextColor = useColorModeValue('custom.800', 'custom.900');
  const expiringIconColor = useColorModeValue('gray.500', 'gray.300');

  const fetchUserData = useCallback(async () => {
    try {
      const userResponse = await getUserData(userInfo?._id).unwrap();
      setUserData(userResponse.user);
    } catch (err) {
      console.error('Error fetching user data:', err);
    }
  }, [userInfo?._id, getUserData]);

  const fetchExpiringSoonTransactions = useCallback(async () => {
    try {
      setExpiringLoading(true);
      const response = await getPointsExpiringSoonTransactions(userInfo?._id).unwrap();
      setExpiringSoonTransactions(response.transactions || []);
    } catch (error) {
      // console.error('Failed to fetch expiring soon transactions', error);
      setExpiringSoonTransactions([]);
    } finally {
      setExpiringLoading(false);
    }
  }, [userInfo?._id, getPointsExpiringSoonTransactions]);

  useEffect(() => {
    fetchUserData();
  }, [fetchUserData]);

  useEffect(() => {
    if (showExpiringSoon) {
      fetchExpiringSoonTransactions();
    }
  }, [showExpiringSoon, fetchExpiringSoonTransactions]);

  useEffect(() => {
    if (!isOpen) {
      setImageLoaded(false);
    }
  }, [isOpen]);

  const QrCodeModal = () => (
    <Modal isOpen={isOpen} onClose={onClose} size="xs">
      <ModalOverlay />
      <ModalContent borderRadius="xl">
        <ModalBody display="flex" flexDirection="column" alignItems="center">
          <Box position="relative" width="300px" height="300px" display="flex" justifyContent="center" alignItems="center">
            {!imageLoaded && <Spinner size="lg" position="absolute" />}
            <Image
              src={userData?.qrCode || ''}
              width="300px"
              height="300px"
              borderRadius="2xl"
              onLoad={() => setImageLoaded(true)}
              opacity={imageLoaded ? 1 : 0}
              alt="QR code"
            />
          </Box>
        </ModalBody>
      </ModalContent>
    </Modal>
  );

  return (
    <Flex
      minH="100vh"
      direction="column"
      align="center"
      justify="center"
      bg={bgColor}
      color={navTextColor}
      width="100%"
      // paddingTop="20px"
      pt={`calc(1rem + env(safe-area-inset-top))`}
    >
      {/* Header */}
      <Box display="flex" bg={bgColor} color={textColor} alignItems="center" p={2} mb={4} width="100%">
        <FontAwesomeIcon
          icon={faArrowLeft}
          size="xl"
          onClick={() => navigate('/')}
          style={{ cursor: 'pointer', marginRight: '8px' }}
        />
        <Text fontSize={{ base: 'lg', md: 'xl' }} fontWeight="bold" ml={2}>
          Scan Me
        </Text>
      </Box>

      {/* Main Content */}
      <Stack minH="100vh" w="full" maxW="lg" borderRadius="2xl" bg={bgColorDark} p={3}>
        {/* QR Code */}
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" p="40px" borderRadius="2xl">
          <Image
            src={userData?.qrCode || ''}
            alt="User QR Code"
            borderRadius="md"
            boxSize={{ base: '150px', md: '200px' }}
            objectFit="cover"
            onClick={onOpen}
            cursor="pointer"
            transition="0.2s"
            _hover={{ boxShadow: 'lg', transform: 'scale(1.02)' }}
          />
        </Box>

        {/* User Info */}
        <Grid templateColumns="repeat(2, 1fr)" gap={4}>
          <GridItem>
            <Text fontSize="md" fontWeight="bold">
              {t('user_points.points')}
            </Text>
            <Text color="gray.400">{userData?.current_points}</Text>
          </GridItem>

          <GridItem>
            <Text fontSize="md" fontWeight="bold">
              {t('user_points.redeemed_points')}
            </Text>
            <Text color="gray.400">{userData?.total_points_redeemed}</Text>
          </GridItem>

          <GridItem colSpan={2}>
            <Divider />
          </GridItem>

          <GridItem colSpan={2}>
            <Text fontSize="md" fontWeight="bold">
              {t('user_points.rewards_account_name')}
            </Text>
            <Text color="gray.400">{userData?.name}</Text>
            <Divider py={2} />
          </GridItem>

          <GridItem colSpan={2}>
            <Text fontSize="md" fontWeight="bold">
              {t('user_points.email')}
            </Text>
            <Text color="gray.400">{userData?.email}</Text>
            <Divider py={2} />
          </GridItem>

          {/* Expiry Link */}
          <GridItem colSpan={2}>
            <Box
              position="relative"
              w="full"
              onClick={() => setShowExpiringSoon(!showExpiringSoon)}
              cursor="pointer"
            >
              <Text fontSize="md" fontWeight="bold">
                {t('user_points.expiry_date')}
              </Text>
              <FontAwesomeIcon
                icon={faChevronRight}
                size="sm"
                style={{
                  position: 'absolute',
                  right: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                }}
              />
            </Box>
          </GridItem>
        </Grid>
      </Stack>

      {/* QR Code Modal */}
      <QrCodeModal />

      <Modal isOpen={showExpiringSoon} onClose={() => setShowExpiringSoon(false)}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('user_points.expiry_date')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {expiringLoading ? (
              // Skeleton list while loading
              [...Array(2)].map((_, i) => (
                <Box
                  key={i}
                  p={3}
                  w="100%"
                  borderBottomWidth="1px"
                  _last={{ borderBottomWidth: 0 }}
                >
                  <HStack justify="space-between" mb={1}>
                    <HStack spacing={2}>
                      <Skeleton height="20px" width="32px" borderRadius="md" />
                      <Skeleton height="16px" width="40px" borderRadius="full" />
                    </HStack>
                    <Skeleton height="16px" width="100px" borderRadius="md" />
                  </HStack>
                </Box>
              ))
            ) : expiringSoonTransactions.length > 0 ? (
              <ExpiringSoonPointHistory
                expiringSoonPointsTransactions={expiringSoonTransactions}
                isLoading={false}
                bgColor={bgColorDark}
                textColor={textColor}
                iconColor={expiringIconColor}
              />
            ) : (
              <Text fontSize="sm" textAlign="center" color="gray.500">
                {t('no_data')}
              </Text>
            )}
            <br />
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* <Modal isOpen={showExpiringSoon} onClose={() => setShowExpiringSoon(false)}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('user_points.expiry_date')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {expiringSoonTransactions.length > 0 ? (
              <ExpiringSoonPointHistory
                expiringSoonPointsTransactions={expiringSoonTransactions}
                isLoading={expiringLoading}
                bgColor={bgColorDark}
                textColor={textColor}
                iconColor={expiringIconColor}
              />
            ) : (
              <Text fontSize="sm" textAlign="center" color="gray.500">
                {t('no_data')}
              </Text>
            )}
            <br/>
          </ModalBody>
        </ModalContent>
      </Modal> */}

    </Flex>
  );
};

export default UserScanMe;
